/**
 * Reduced motion for the whole Panel: Vivo (El Parte) and the staff screens read the same hook, so neither depends on
 * the other for it. Under reduced motion every change lands in its final state at once.
 *
 * `data-vv-still` on the document asks for the final state on request (review, print, screenshots); the attribute keeps
 * its Vivo name because the capture tools and Vivo's stylesheet already set and read it.
 */

import { useSyncExternalStore } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const STILL_ATTRIBUTE = 'data-vv-still';

function reducedMotionQuery(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(REDUCED_MOTION_QUERY) : null;
}

/**
 * True when the reader asked for reduced motion (or the page asked to stand still).
 * Exported for its spec.
 */
export function prefersReducedMotion(): boolean {
  if (typeof document !== 'undefined' && document.documentElement.hasAttribute(STILL_ATTRIBUTE)) return true;
  return reducedMotionQuery()?.matches ?? false;
}

function subscribeReducedMotion(onChange: () => void): () => void {
  const query = reducedMotionQuery();
  query?.addEventListener('change', onChange);
  return () => query?.removeEventListener('change', onChange);
}

/** Follows `prefers-reduced-motion` live. */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, prefersReducedMotion, () => false);
}
